import React from "react";

/**
 * ErrorBoundary — catches unhandled JS exceptions in the React tree.
 * Prevents full SPA crash by showing a fallback UI.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary fallback={<CustomFallback />}>
 *     <DashboardSection />
 *   </ErrorBoundary>
 *
 * Props:
 *   - children: React nodes to wrap
 *   - fallback: optional custom fallback UI (ReactNode)
 *   - onError: optional callback (error, errorInfo) => void
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log for debugging; future: send to error tracking service (Sentry, etc.)
    console.error("[ErrorBoundary] Uncaught error:", error);
    console.error("[ErrorBoundary] Component stack:", errorInfo?.componentStack);

    if (typeof this.props.onError === "function") {
      this.props.onError(error, errorInfo);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Allow custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#0f1117",
            color: "#e5e7eb",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            padding: "24px",
          }}
        >
          <div
            style={{
              textAlign: "center",
              maxWidth: "440px",
            }}
          >
            {/* Icon */}
            <div
              style={{
                fontSize: "48px",
                marginBottom: "16px",
                opacity: 0.6,
              }}
            >
              &#9888;
            </div>

            <h1
              style={{
                fontSize: "24px",
                fontWeight: 600,
                marginBottom: "12px",
                color: "#f9fafb",
              }}
            >
              Something went wrong
            </h1>

            <p
              style={{
                fontSize: "15px",
                color: "#9ca3af",
                marginBottom: "32px",
                lineHeight: 1.5,
              }}
            >
              An unexpected error occurred. Please reload the page to continue.
            </p>

            {/* Show error message in dev mode */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <pre
                style={{
                  textAlign: "left",
                  backgroundColor: "#1a1d27",
                  border: "1px solid #374151",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  fontSize: "13px",
                  color: "#f87171",
                  marginBottom: "24px",
                  overflow: "auto",
                  maxHeight: "160px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {this.state.error.toString()}
              </pre>
            )}

            <button
              onClick={this.handleReload}
              style={{
                backgroundColor: "#6366f1",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                padding: "12px 32px",
                fontSize: "15px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "background-color 0.15s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#4f46e5")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "#6366f1")
              }
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
